export interface DecodedBitmap {
  width: number
  height: number
  close: () => void
}

export interface DrawOptions {
  preserveCommittedPreview?: boolean
  shouldCommit?: () => boolean
}

/** Releases the canvas backing store after its pixels are no longer needed. */
export function releaseCanvasBackingStore(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return
  canvas.width = 0
  canvas.height = 0
}

export class BitmapSurface {
  private pending: DecodedBitmap | null = null

  setPending(bitmap: DecodedBitmap): void {
    this.pending?.close()
    this.pending = bitmap
  }

  draw(canvas: HTMLCanvasElement, width: number, height: number, options: DrawOptions = {}): boolean {
    const bitmap = this.pending
    if (!bitmap) return false
    let committedPreview: HTMLCanvasElement | null = null
    let committedPreviewCopy: HTMLCanvasElement | null = null
    let candidate: HTMLCanvasElement | null = null
    try {
      // Stage the pending bitmap away from the visible surface first. This
      // means a decode/draw failure cannot mutate the last committed preview.
      candidate = document.createElement('canvas')
      candidate.width = width
      candidate.height = height
      const candidateContext = candidate.getContext('2d')
      if (!candidateContext) return false
      candidateContext.drawImage(bitmap as ImageBitmap, 0, 0)

      if (options.shouldCommit && !options.shouldCommit()) return false
      const context = canvas.getContext('2d')
      if (!context) return false
      if (options.preserveCommittedPreview && canvas.width > 0 && canvas.height > 0) {
        committedPreviewCopy = document.createElement('canvas')
        committedPreviewCopy.width = canvas.width
        committedPreviewCopy.height = canvas.height
        const committedContext = committedPreviewCopy.getContext('2d')
        if (!committedContext) return false
        committedContext.drawImage(canvas, 0, 0)
        // Do not publish a backup until copying the visible preview has
        // completed. A failed copy leaves this canvas blank and must not
        // replace the committed preview during recovery.
        committedPreview = committedPreviewCopy
        committedPreviewCopy = null
      }
      if (options.shouldCommit && !options.shouldCommit()) return false
      canvas.width = width
      canvas.height = height
      context.drawImage(candidate, 0, 0)
      return true
    } catch {
      if (committedPreview) {
        // Recovery must stay best-effort: a persistent canvas failure can
        // affect both the commit and the restoration draw. `draw()` is a
        // boolean boundary for callers, so never leak that second failure.
        try {
          canvas.width = committedPreview.width
          canvas.height = committedPreview.height
          const restoreContext = canvas.getContext('2d')
          restoreContext?.drawImage(committedPreview, 0, 0)
        } catch {
          // The existing preview cannot be restored when the canvas remains
          // unavailable. The caller records the controlled draw failure.
        }
      }
      return false
    } finally {
      releaseCanvasBackingStore(committedPreview)
      releaseCanvasBackingStore(committedPreviewCopy)
      releaseCanvasBackingStore(candidate)
      // A replacement/reset can run re-entrantly from browser canvas hooks.
      // In that case it has already closed and cleared this pending bitmap.
      if (this.pending === bitmap) {
        bitmap.close()
        this.pending = null
      }
    }
  }

  dispose(): void {
    this.pending?.close()
    this.pending = null
  }
}
